import type { TelopFont, TitleCardOverlay, VenueId } from '@shared/types'
import { useEditorStore } from '../../../../store/editorStore'
import { CheckboxField, SelectField, SliderField, TextField } from '../../../common/Field'
import { InspectorFrame } from './InspectorFrame'
import { OVERLAY_KIND_LABEL } from '../overlayMeta'
import { FONT_SELECT_LABEL, FONT_SELECT_ORDER } from './fontOptions'

const VENUE_LABEL: Record<VenueId, string> = {
  ginza: '銀座 (GINZA)',
  nagoya: '名古屋 (NAGOYA)',
  saitama: 'さいたま (SAITAMA)',
  generic: '汎用',
}

/** 旧 TabTitle.tsx の単体編集フィールドを選択駆動インスペクタへ切り出したもの。 */
export function TitleInspector({ overlay }: { overlay: TitleCardOverlay }) {
  const updateOverlay = useEditorStore((s) => s.updateOverlay)
  function patch(p: Partial<TitleCardOverlay>) {
    updateOverlay(overlay.id, p)
  }

  return (
    <InspectorFrame
      title={OVERLAY_KIND_LABEL.title}
      overlayId={overlay.id}
      overlayKind="title"
      startSec={overlay.startSec}
      durationSec={overlay.durationSec}
      minDurationSec={0.5}
      maxDurationSec={10}
    >
      <TextField label="コピー" value={overlay.text} onChange={(e) => patch({ text: e.target.value })} />

      <div className="grid grid-cols-2 gap-2">
        <TextField
          label="強調文字"
          maxLength={1}
          value={overlay.emphasis?.char ?? ''}
          placeholder="例: 夏"
          onChange={(e) =>
            patch({
              emphasis: {
                char: e.target.value,
                color: overlay.emphasis?.color ?? '#E6B422',
                scale: overlay.emphasis?.scale ?? 1.6,
              },
            })
          }
        />
        <div>
          <label className="block text-[11px] font-medium text-text-muted mb-1">強調色</label>
          <input
            type="color"
            value={overlay.emphasis?.color ?? '#E6B422'}
            onChange={(e) =>
              patch({
                emphasis: {
                  char: overlay.emphasis?.char ?? '',
                  color: e.target.value,
                  scale: overlay.emphasis?.scale ?? 1.6,
                },
              })
            }
            className="w-full h-[30px] rounded bg-ink-bg/60 border border-ink-border cursor-pointer"
          />
        </div>
      </div>

      <SliderField
        label="強調倍率"
        value={overlay.emphasis?.scale ?? 1}
        min={1}
        max={2.5}
        step={0.1}
        onChange={(scale) =>
          patch({
            emphasis: { char: overlay.emphasis?.char ?? '', color: overlay.emphasis?.color ?? '#E6B422', scale },
          })
        }
      />

      <CheckboxField label="縦書き" checked={overlay.vertical} onChange={(vertical) => patch({ vertical })} />

      <TextField
        label="季節バッジ"
        value={overlay.seasonBadge ?? ''}
        placeholder="例: 初夏の企画展"
        onChange={(e) => patch({ seasonBadge: e.target.value })}
      />
      <TextField
        label="会期"
        value={overlay.dateText ?? ''}
        placeholder="例: 2026.6.26 FRI - 9.23 WED"
        onChange={(e) => patch({ dateText: e.target.value })}
      />
      <SelectField label="会場" value={overlay.venue} onChange={(e) => patch({ venue: e.target.value as VenueId })}>
        {(Object.keys(VENUE_LABEL) as VenueId[]).map((v) => (
          <option key={v} value={v}>
            {VENUE_LABEL[v]}
          </option>
        ))}
      </SelectField>
      <SelectField
        label="位置"
        value={overlay.position}
        onChange={(e) => patch({ position: e.target.value as 'center' | 'right' })}
      >
        <option value="center">中央</option>
        <option value="right">右寄せ</option>
      </SelectField>
      {/* P10-1: 未定義は「ゴシック」として表示（shared/src/types.ts font? doc: default 'gothic'）。 */}
      <SelectField
        label="フォント"
        value={overlay.font ?? 'gothic'}
        onChange={(e) => patch({ font: e.target.value as TelopFont })}
        data-testid="overlay-font-select"
      >
        {FONT_SELECT_ORDER.map((f) => (
          <option key={f} value={f}>
            {FONT_SELECT_LABEL[f]}
          </option>
        ))}
      </SelectField>
    </InspectorFrame>
  )
}
